import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../../../lib/toast';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate } from '../finance/shared';
import CoachProfileModal from './CoachProfileModal';

const OFFER_STATUS_LABELS = { sent: 'مرسل', accepted: 'مقبول من المدرب', rejected: 'مرفوض', cancelled: 'ملغي', expired: 'منتهي' };
const OFFER_STATUS_COLORS = { sent: 'bg-blue-50 text-blue-700', accepted: 'bg-teal-50 text-teal-700', rejected: 'bg-red-50 text-red-600', cancelled: 'bg-slate-100 text-slate-500' };
const EMPTY_FINALIZE = { title: '', sport: 'football', team_ids: [], category_ids: [], season: '', start_date: '', employee_id: '', salary_amount: '', contract_note: '' };

export default function CoachOrgOffersTab() {
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [finalizeModal, setFinalizeModal] = useState(null);
  const [form, setForm] = useState(EMPTY_FINALIZE);
  const [profileCoachId, setProfileCoachId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, t, c, e] = await Promise.all([
        axios.get(`${API}/coaches/offers`, auth()),
        axios.get(`${API}/teams`, auth()),
        axios.get(`${API}/categories`, auth()),
        axios.get(`${API}/employees`, auth()),
      ]);
      setItems(o.data); setTeams(t.data); setCategories(c.data); setEmployees(e.data);
    } catch { setItems([]); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const cancelOffer = async (id) => {
    try { await axios.put(`${API}/coaches/offers/${id}/cancel`, {}, auth()); toast.success('تم إلغاء العرض'); load(); }
    catch (err) { toast.error(errMsg(err, 'فشل الإلغاء')); }
  };

  const openFinalize = (offer) => {
    setFinalizeModal(offer);
    setForm({ ...EMPTY_FINALIZE, title: offer.title, sport: offer.sport, season: offer.season || '', start_date: offer.start_date, salary_amount: offer.salary_amount || '' });
  };

  const toggleMulti = (key, val) => setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((x) => x !== val) : [...f[key], val] }));

  const submitFinalize = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/coaches/memberships/finalize`, {
        offer_id: finalizeModal.id, title: form.title, sport: form.sport, team_ids: form.team_ids, category_ids: form.category_ids,
        season: form.season, start_date: form.start_date, employee_id: form.employee_id || null,
        salary_amount: form.salary_amount ? Number(form.salary_amount) : null, contract_note: form.contract_note,
      }, auth());
      toast.success('تم إكمال بيانات التعاقد — يمكنك تفعيله من تبويب الطاقم التدريبي');
      setFinalizeModal(null); load();
    } catch (err) { toast.error(errMsg(err, 'فشل إكمال التعاقد')); }
  };

  return (
    <div className="space-y-4" data-testid="coach-org-offers-tab">
      {loading ? <p className="text-sm text-slate-400">جارِ التحميل...</p> : items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400" data-testid="coach-org-offers-empty">لا توجد عروض مرسلة بعد</p>
      ) : (
        <div className="space-y-2">
          {items.map((o) => (
            <div key={o.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid="coach-org-offer-row">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-800">{o.title} — {o.coach_name}</div>
                  <div className="text-xs text-slate-500">يبدأ {fmtDate(o.start_date)}</div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${OFFER_STATUS_COLORS[o.status] || 'bg-slate-100 text-slate-500'}`}>{OFFER_STATUS_LABELS[o.status] || o.status}</span>
              </div>
              <div className="mt-3 flex gap-2">
                <button onClick={() => setProfileCoachId(o.coach_id)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:border-teal-300" data-testid="coach-org-offer-profile-btn">السيرة الذاتية</button>
                {o.status === 'sent' && <button onClick={() => cancelOffer(o.id)} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600">إلغاء العرض</button>}
                {o.status === 'accepted' && <button onClick={() => openFinalize(o)} className="rounded-lg bg-[#16233b] px-3 py-1.5 text-xs font-semibold text-white" data-testid="coach-org-finalize-btn">إكمال التعاقد</button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={Boolean(finalizeModal)} title="إكمال بيانات التعاقد" onClose={() => setFinalizeModal(null)} wide>
        <form onSubmit={submitFinalize} className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="المسمى الوظيفي" required><input required className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="finalize-input-title" /></Field>
          <Field label="الموسم"><input className={inputCls} value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} data-testid="finalize-input-season" /></Field>
          <Field label="تاريخ البداية" required><input required type="date" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="finalize-input-start-date" /></Field>
          <Field label="ربط بموظف موجود (اختياري)">
            <select className={inputCls} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} data-testid="finalize-employee-select">
              <option value="">بدون ربط — بدون تكرار</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.full_name || emp.name}</option>)}
            </select>
          </Field>
          <div className="col-span-full">
            <span className="mb-1 block text-xs font-semibold text-slate-600">الفرق المسندة</span>
            <div className="flex flex-wrap gap-2">
              {teams.map((t) => (
                <button type="button" key={t.id} onClick={() => toggleMulti('team_ids', t.id)} className={`rounded-full px-3 py-1 text-xs font-semibold ${form.team_ids.includes(t.id) ? 'bg-[#16233b] text-white' : 'border border-slate-200 text-slate-600'}`}>{t.name}</button>
              ))}
            </div>
          </div>
          <div className="col-span-full">
            <span className="mb-1 block text-xs font-semibold text-slate-600">الفئات المسندة</span>
            <div className="flex flex-wrap gap-2">
              {categories.map((c) => (
                <button type="button" key={c.id} onClick={() => toggleMulti('category_ids', c.id)} className={`rounded-full px-3 py-1 text-xs font-semibold ${form.category_ids.includes(c.id) ? 'bg-[#16233b] text-white' : 'border border-slate-200 text-slate-600'}`}>{c.name}</button>
              ))}
            </div>
          </div>
          <Field label="الراتب (اختياري)"><input type="number" className={inputCls} value={form.salary_amount} onChange={(e) => setForm({ ...form, salary_amount: e.target.value })} data-testid="finalize-input-salary" /></Field>
          <Field label="ملاحظات العقد"><input className={inputCls} value={form.contract_note} onChange={(e) => setForm({ ...form, contract_note: e.target.value })} data-testid="finalize-input-note" /></Field>
          <div className="col-span-full flex justify-end gap-2">
            <button type="button" onClick={() => setFinalizeModal(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="finalize-submit-btn">حفظ بيانات التعاقد</PrimaryBtn>
          </div>
        </form>
      </Modal>
      <CoachProfileModal coachId={profileCoachId} onClose={() => setProfileCoachId(null)} />
    </div>
  );
}
